import type { BlockCaptionEditor } from '@blocksuite/affine-components/caption';
import { LoadingIcon } from '@blocksuite/affine-components/icons';
import { Peekable } from '@blocksuite/affine-components/peek';
import { ResourceController } from '@blocksuite/affine-components/resource';
import {
  type ImageBlockModel,
  ImageBlockSchema,
} from '@blocksuite/affine-model';
import { EDGELESS_CANVAS_CARD_SHADOW } from '@blocksuite/affine-shared/consts';
import {
  FontLoaderService,
  isCanvasHandwritingFontReady,
  waitForCanvasHandwritingFont,
} from '@blocksuite/affine-shared/services';
import { cssVarV2, unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { formatSize, stopPropagation } from '@blocksuite/affine-shared/utils';
import { BrokenImageIcon, ImageIcon } from '@blocksuite/icons/lit';
import { GfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { computed, signal } from '@preact/signals-core';
import { css, html, nothing } from 'lit';
import { query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { when } from 'lit/directives/when.js';

import {
  imageEmbeddingBadgeStyles,
  ImageEmbeddingStatusController,
} from './image-embedding-status';
import {
  copyImageBlob,
  downloadImageBlob,
  refreshData,
  turnImageIntoCardView,
} from './utils';

@Peekable()
export class ImageEdgelessBlockComponent extends GfxBlockComponent<ImageBlockModel> {
  static override styles = [
    imageEmbeddingBadgeStyles,
    css`
      affine-edgeless-image {
        position: relative;
      }

      affine-edgeless-image .affine-image-loading-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 4px;
        right: 4px;
        width: 36px;
        height: 36px;
        padding: 5px;
        border-radius: 8px;
        background: ${unsafeCSSVarV2(
          'loading/imageLoadingBackground',
          '#92929238'
        )};

        & > svg {
          font-size: 25.71px;
        }
      }

      affine-edgeless-image .affine-image-status {
        position: absolute;
        z-index: 4;
        left: 18px;
        bottom: 18px;
      }

      affine-edgeless-image .polaroid > .affine-image-status {
        top: 28px;
        bottom: auto;
        left: 28px;
      }

      affine-edgeless-image .resizable-img,
      affine-edgeless-image .resizable-img img {
        width: 100%;
        height: 100%;
      }

      affine-edgeless-image .resizable-img {
        position: relative;
      }

      affine-edgeless-image .affine-image-container.polaroid {
        box-sizing: border-box;
        flex-direction: column;
        gap: 7px;
        padding: 10px 10px 9px;
        overflow: visible;
        border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
        border-radius: 3px 2px 4px 3px / 2px 4px 3px 3px;
        background-color: var(--streamlain-v2-layer-background-primary);
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'%3E%3Cfilter id='paper'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='3' seed='17' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23paper)' opacity='.055'/%3E%3C/svg%3E");
        background-size: 180px 180px;
      }

      affine-edgeless-image .affine-image-container.polaroid::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 2;
        pointer-events: none;
        background: linear-gradient(
          118deg,
          rgb(255 255 255 / 13%),
          transparent 24% 78%,
          rgb(78 63 42 / 4%)
        );
      }

      affine-edgeless-image .polaroid .resizable-img {
        position: relative;
        flex: 1;
        min-height: 0;
        width: 100%;
        overflow: visible;
        background: #d8d2c6;
        border: 1px solid rgb(48 42 34 / 22%);
        box-shadow:
          0 1px 2px rgb(0 0 0 / 28%) inset,
          0 0 0 1px rgb(255 255 255 / 18%);
      }

      affine-edgeless-image .polaroid .resizable-img::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        background: linear-gradient(
          132deg,
          rgb(255 255 255 / 14%) 0,
          rgb(255 255 255 / 3%) 28%,
          transparent 52% 100%
        );
        mix-blend-mode: screen;
      }

      affine-edgeless-image .polaroid .resizable-img img {
        display: block;
        object-fit: cover;
      }

      affine-edgeless-image .polaroid-footer {
        position: relative;
        z-index: 3;
        display: flex;
        min-height: 48px;
        flex: none;
        flex-direction: column;
        justify-content: flex-start;
        width: 100%;
        padding: 0 6px 2px;
        box-sizing: border-box;
        color: var(--streamlain-v2-text-primary);
      }

      affine-edgeless-image
        .affine-image-container.polaroid[data-font-ready='false']
        .polaroid-footer {
        visibility: hidden;
      }

      affine-edgeless-image .polaroid-footer .block-caption-editor {
        margin: 0;
        color: var(--streamlain-v2-text-primary);
        font-family: 'Kalam', cursive;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        text-align: left;
      }

      affine-edgeless-image .polaroid-footer block-caption-editor {
        display: block;
        width: 100%;
        flex: none;
      }

      affine-edgeless-image
        .polaroid-footer
        block-caption-editor.polaroid-source-editor {
        height: 13px;
        min-height: 13px;
        flex: 0 0 13px;
      }

      affine-edgeless-image .polaroid-source-editor .block-caption-editor {
        field-sizing: fixed;
        height: 13px;
        min-height: 13px;
        max-height: 13px;
        margin: 0;
        box-sizing: border-box;
        overflow: hidden;
        color: var(--streamlain-v2-text-secondary);
        font:
          9px/12px Inter,
          sans-serif;
        text-align: left;
        opacity: 0.62;
        transition: opacity 120ms ease;
      }

      affine-edgeless-image
        .polaroid-source-editor
        .block-caption-editor:placeholder-shown:not(:focus) {
        opacity: 0;
      }

      affine-edgeless-image
        .polaroid-footer:hover
        .polaroid-source-editor
        .block-caption-editor:placeholder-shown,
      affine-edgeless-image
        .polaroid-source-editor
        .block-caption-editor:focus {
        opacity: 0.48;
      }

      affine-edgeless-image .image-format-badge {
        position: absolute;
        bottom: 6px;
        left: 6px;
        z-index: 1;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        padding: 3px 6px;
        border-radius: 3px;
        background: var(--streamlain-v2-layer-background-primary);
        color: var(--streamlain-v2-text-primary);
        pointer-events: none;
        letter-spacing: 0.3px;
        opacity: 0;
        transition: opacity 0.15s ease;
      }

      affine-edgeless-image .resizable-img:hover .image-format-badge {
        opacity: 1;
      }
    `,
  ];

  /** sl-luoo: Detected image format for badge overlay (e.g. "HEIC", "JPG"). */
  imageFormat$ = signal<string | null>(null);

  @state() private accessor _fontReady = isCanvasHandwritingFontReady();

  resourceController = new ResourceController(
    computed(() => this.model.props.sourceId$.value),
    'Image'
  );

  embeddingStatus = new ImageEmbeddingStatusController(this);

  get blobUrl() {
    return this.resourceController.blobUrl$.value;
  }

  convertToCardView = () => {
    turnImageIntoCardView(this).catch(console.error);
  };

  copy = () => {
    copyImageBlob(this).catch(console.error);
  };

  download = () => {
    downloadImageBlob(this).catch(console.error);
  };

  refreshData = () => {
    refreshData(this).catch(console.error);
  };

  private _handleError() {
    this.resourceController.updateState({
      errorMessage: 'Failed to download image!',
    });
  }

  private _activatePolaroidEditing(event: Event) {
    event.stopPropagation();
    this.gfx.selection.set({
      elements: [this.model.id],
      editing: true,
    });
  }

  private _showPolaroidCaption(event: PointerEvent) {
    const path = event.composedPath();
    if (
      path.some(
        target =>
          target instanceof Element &&
          (target.matches('.polaroid-source-editor') ||
            target.matches('.block-caption-editor'))
      )
    ) {
      return;
    }

    this._activatePolaroidEditing(event);
    this.captionEditor?.show();
  }

  override connectedCallback() {
    super.connectedCallback();

    this.contentEditable = 'false';

    this.resourceController.setEngine(this.std.store.blobSync);

    this.disposables.add(this.resourceController.subscribe());
    this.disposables.add(this.resourceController);

    this.disposables.add(
      this.model.props.sourceId$.subscribe(() => {
        this.refreshData();
        this.embeddingStatus.refresh();
      })
    );

    this.embeddingStatus.connect();

    void waitForCanvasHandwritingFont(
      this.std.getOptional(FontLoaderService)
    ).then(
      () => {
        if (this.isConnected) this._fontReady = true;
      },
      () => {
        if (this.isConnected) this._fontReady = true;
      }
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.embeddingStatus.disconnect();
  }

  override renderGfxBlock() {
    const blobUrl = this.blobUrl;
    const {
      rotate = 0,
      size = 0,
      caption = 'Image',
      presentation = 'default',
    } = this.model.props;

    const containerStyleMap = styleMap({
      display: 'flex',
      position: 'relative',
      width: '100%',
      height: '100%',
      transform: `rotate(${rotate}deg)`,
      transformOrigin: 'center',
      boxShadow:
        presentation === 'polaroid' ? EDGELESS_CANVAS_CARD_SHADOW : 'none',
    });

    const resovledState = this.resourceController.resolveStateWith({
      loadingIcon: LoadingIcon({
        strokeColor: cssVarV2('button/pureWhiteText'),
        ringColor: cssVarV2('loading/imageLoadingLayer', '#ffffff8f'),
      }),
      errorIcon: BrokenImageIcon(),
      icon: ImageIcon(),
      title: 'Image',
      description: formatSize(size),
    });

    const { loading, icon, description, error, needUpload } = resovledState;

    return html`
      <div
        class="affine-image-container ${presentation === 'polaroid'
          ? 'polaroid'
          : ''}"
        data-font-ready=${this._fontReady}
        style=${containerStyleMap}
      >
        ${when(
          blobUrl,
          () => html`
            <div class="resizable-img">
              <img
                class="drag-target"
                draggable="false"
                loading="lazy"
                src=${blobUrl}
                alt=${caption}
                @error=${this._handleError}
              />
              ${when(
                this.imageFormat$.value,
                () =>
                  html`<div class="image-format-badge">
                    ${this.imageFormat$.value}
                  </div>`
              )}
              ${this.embeddingStatus.render()}
            </div>
            ${when(
              loading,
              () => html`<div class="affine-image-loading-icon">${icon}</div>`
            )}
          `,
          () =>
            html`<affine-image-fallback-card
              .state=${resovledState}
            ></affine-image-fallback-card>`
        )}
        ${when(
          Boolean(error && description),
          () =>
            html`<affine-resource-status
              class="affine-image-status"
              .message=${description}
              .needUpload=${needUpload}
              .action=${() =>
                needUpload
                  ? this.resourceController.upload()
                  : this.refreshData()}
            ></affine-resource-status>`
        )}
        ${presentation === 'polaroid'
          ? html`<div
              class="polaroid-footer"
              @pointerdown=${this._showPolaroidCaption}
              @click=${stopPropagation}
              @dblclick=${stopPropagation}
            >
              <block-caption-editor .persistent=${true}></block-caption-editor>
              <block-caption-editor
                class="polaroid-source-editor"
                .persistent=${true}
                .propertyKey=${'sourceCaption'}
                .placeholder=${'Add image source · optional'}
              ></block-caption-editor>
            </div>`
          : nothing}
        <affine-block-selection .block=${this}></affine-block-selection>
      </div>
      ${presentation === 'polaroid'
        ? nothing
        : html`<block-caption-editor></block-caption-editor>`}
      ${Object.values(this.widgets)}
    `;
  }

  @query('block-caption-editor')
  accessor captionEditor!: BlockCaptionEditor | null;

  @query('block-caption-editor.polaroid-source-editor')
  accessor sourceEditor!: BlockCaptionEditor | null;

  @query('.resizable-img')
  accessor resizableImg!: HTMLDivElement;
}

export const ImageEdgelessBlockInteraction =
  GfxViewInteractionExtension<ImageEdgelessBlockComponent>(
    ImageBlockSchema.model.flavour,
    {
      selectionBorderRadius: ({ model }) =>
        model.props.presentation === 'polaroid' ? 3 : 0,
      resizeConstraint: {
        lockRatio: true,
      },
      handleSelection: ({ gfx, model, view }) => ({
        onSelect(context) {
          const eventPath = context.event.composedPath();
          const isSourceEditor = eventPath.some(
            target =>
              target instanceof Element &&
              target.matches('.polaroid-source-editor')
          );
          const isCaptionEditor = eventPath.some(
            target =>
              target instanceof Element &&
              target.matches('block-caption-editor, .block-caption-editor')
          );
          const isPolaroidEditor =
            model.props.presentation === 'polaroid' &&
            eventPath.some(
              target =>
                target instanceof Element && target.matches('.polaroid-footer')
            );

          if (isPolaroidEditor) {
            if (model.isLocked()) return;
            if (gfx.selection.has(model.id) && gfx.selection.editing)
              return true;

            gfx.selection.set({ elements: [model.id], editing: true });
            void view.updateComplete
              .then(() => {
                if (!view.isConnected) return;
                if (isSourceEditor) {
                  view.sourceEditor?.show();
                } else if (isCaptionEditor) {
                  view.captionEditor?.show();
                }
              })
              .catch(console.error);
            return true;
          }

          context.default(context);
          return true;
        },
      }),
    }
  );

declare global {
  interface HTMLElementTagNameMap {
    'affine-edgeless-image': ImageEdgelessBlockComponent;
  }
}
